import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ArrowRight,
  PartyPopper,
  X as XIcon,
  Plug,
  Mic2,
  Sparkles,
} from "lucide-react";
import { api } from "../lib/api";

interface OnboardingState {
  hasSocial: boolean;
  hasVoice: boolean;
  hasProject: boolean;
  loading: boolean;
}

const DISMISS_KEY = "be:onboarding-dismissed-v1";

export function OnboardingChecklist() {
  const [state, setState] = useState<OnboardingState>({
    hasSocial: false,
    hasVoice: false,
    hasProject: false,
    loading: true,
  });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    Promise.all([
      api
        .get<{ social_accounts: unknown[] }>("/api/social-accounts")
        .catch(() => ({ social_accounts: [] })),
      api
        .get<{ voice_profiles: unknown[] }>("/api/voice-profiles")
        .catch(() => ({ voice_profiles: [] })),
      api
        .get<{ projects: unknown[] }>("/api/projects")
        .catch(() => ({ projects: [] })),
    ]).then(([accts, voices, projects]) => {
      if (!alive) return;
      setState({
        hasSocial: (accts.social_accounts?.length ?? 0) > 0,
        hasVoice: (voices.voice_profiles?.length ?? 0) > 0,
        hasProject: (projects.projects?.length ?? 0) > 0,
        loading: false,
      });
    });
    return () => {
      alive = false;
    };
  }, [dismissed]);

  function dismiss(): void {
    window.localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  if (dismissed || state.loading) return null;

  const completed = [state.hasSocial, state.hasVoice, state.hasProject].filter(
    Boolean
  ).length;
  const total = 3;
  const allDone = completed === total;

  if (allDone && window.localStorage.getItem(DISMISS_KEY) !== "1") {
    return (
      <div className="mb-6 flex items-center justify-between rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/80 to-white p-4 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <PartyPopper className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-900">
              You're fully set up
            </p>
            <p className="text-xs text-emerald-800/80">
              Voice trained · account connected · first post shipped. Welcome
              aboard.
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-emerald-700 hover:text-emerald-900"
          title="Dismiss"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const progressPct = (completed / total) * 100;

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-brand-200/60 bg-gradient-to-br from-brand-50/40 via-white to-white shadow-sm">
      <div className="flex items-center justify-between border-b border-brand-100/70 bg-white/60 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-sm">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-ink-900">Getting started</p>
            <p className="text-xs text-ink-500">
              {completed} of {total} complete · finish in ~5 minutes
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-ink-400 transition-colors hover:text-ink-700"
          title="Hide checklist"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="h-1.5 w-full bg-ink-100">
        <div
          className="h-full bg-brand-gradient transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="grid gap-2 p-3 sm:grid-cols-3 sm:p-4">
        <Step
          done={state.hasSocial}
          Icon={Plug}
          title="Connect an account"
          desc="X, Bluesky, or LinkedIn"
          to="/app/settings"
          ctaLabel="Connect"
        />
        <Step
          done={state.hasVoice}
          Icon={Mic2}
          title="Train your voice"
          desc="Paste 3-10 past posts"
          to="/app/voices/new"
          ctaLabel="Train voice"
        />
        <Step
          done={state.hasProject}
          Icon={Sparkles}
          title="Generate your first post"
          desc="One paste, five variants"
          to="/app/new"
          ctaLabel="Generate"
        />
      </div>
    </div>
  );
}

function Step({
  done,
  Icon,
  title,
  desc,
  to,
  ctaLabel,
}: {
  done: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  to: string;
  ctaLabel: string;
}) {
  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-200/60 bg-emerald-50/60 p-3.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-sm">
          <Check className="h-4 w-4" strokeWidth={3} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-emerald-900 line-through decoration-emerald-300/70">
            {title}
          </p>
          <p className="truncate text-2xs text-emerald-800/70">Done</p>
        </div>
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-xl border border-ink-200/70 bg-white p-3.5 no-underline shadow-xs transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-600 transition-colors group-hover:bg-brand-gradient group-hover:text-white">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink-900">{title}</p>
        <p className="truncate text-2xs text-ink-500">{desc}</p>
      </div>
      <span className="inline-flex items-center gap-1 text-2xs font-medium text-brand-700">
        {ctaLabel}
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
